import {
  Anchor,
  Box,
  Button,
  Center,
  PasswordInput,
  Stack,
  Text,
  Title,
  Alert,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useState } from 'react';
import { IconAlertCircle, IconCircleCheck, IconLock } from '@tabler/icons-react';
import { apiClient } from '../api/http';
import { getErrorMessage } from '../api/client';

/**
 * Sprint 5.2: pagina publica para resetear la password usando el token
 * recibido por email. Si el token falta, expiró o ya se usó, el backend
 * devuelve 400 y mostramos mensaje específico.
 *
 * En OK: navega a /login tras 2s para que el usuario entre con la nueva password.
 */
export function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const form = useForm({
    initialValues: { newPassword: '', confirm: '' },
    validate: {
      newPassword: (v) =>
        v.length >= 8 ? null : 'La password debe tener al menos 8 caracteres',
      confirm: (v, vals) =>
        v === vals.newPassword ? null : 'Las passwords no coinciden',
    },
  });

  const handleSubmit = async (values: typeof form.values) => {
    if (!token) {
      setError('Falta el token de recuperación en la URL.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await apiClient.post('/auth/reset-password', {
        token,
        newPassword: values.newPassword,
      });
      setDone(true);
      setTimeout(() => navigate('/login', { replace: true }), 2000);
    } catch (e) {
      setError(getErrorMessage(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Center mih="100vh" bg="deca.0">
      <Box p="xl" bg="white" style={{ borderRadius: 12, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
        <Stack gap="md" maw={400}>
          <Stack align="center" gap={4}>
            <IconLock size={32} color="var(--mantine-color-deca-9)" />
            <Title order={2} c="deca.9" ta="center">
              Crear nueva password
            </Title>
          </Stack>

          {done ? (
            <Alert color="green" variant="light" icon={<IconCircleCheck size={20} />}>
              <Stack gap={4}>
                <Text fw={600}>Password actualizada</Text>
                <Text size="sm">
                  En unos segundos te llevamos al inicio de sesion para que entres con
                  tu nueva password.
                </Text>
              </Stack>
            </Alert>
          ) : (
            <>
              {!token && (
                <Alert color="yellow" variant="light" icon={<IconAlertCircle size={20} />}>
                  Falta el token de recuperacion en la URL. Abre el enlace que te
                  hemos enviado por email.
                </Alert>
              )}
              {error && (
                <Alert color="red" variant="light" icon={<IconAlertCircle size={20} />}>
                  {error}
                </Alert>
              )}
              <form onSubmit={form.onSubmit(handleSubmit)}>
                <Stack>
                  <PasswordInput
                    label="Nueva password"
                    description="Minimo 8 caracteres."
                    required
                    {...form.getInputProps('newPassword')}
                  />
                  <PasswordInput
                    label="Repetir nueva password"
                    required
                    {...form.getInputProps('confirm')}
                  />
                  <Button type="submit" loading={submitting} color="deca.9" fullWidth>
                    Guardar nueva password
                  </Button>
                  <Text size="sm" c="dimmed" ta="center">
                    <Anchor component={Link} to="/login">
                      Volver al inicio de sesion
                    </Anchor>
                  </Text>
                </Stack>
              </form>
            </>
          )}
        </Stack>
      </Box>
    </Center>
  );
}